import type { ErrorCode } from '@shared/types/api'

export class ApiClientError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly details: unknown

  constructor(status: number, code: ErrorCode, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
    this.details = details
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /**
   * Overrides the content type and sends `body` as-is. Used for audio uploads,
   * where JSON-encoding a Blob would silently produce `{}`.
   */
  contentType?: string
}

/**
 * Single entry point for every API call. Credentials are always included so the
 * session cookie rides along; same-origin means no CORS and no token juggling.
 */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, contentType } = options

  // A raw body (Blob, ArrayBuffer) is passed straight through; everything else
  // is JSON. Without this, `JSON.stringify(blob)` yields "{}" and the upload
  // arrives empty with no error anywhere to explain it.
  const isRaw = contentType !== undefined
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: isRaw
      ? { 'content-type': contentType }
      : body
        ? { 'content-type': 'application/json' }
        : undefined,
    body: isRaw ? (body as BodyInit) : body ? JSON.stringify(body) : undefined,
    signal,
  })

  if (res.status === 204) return undefined as T

  const payload = await res.json().catch(() => null)

  if (!res.ok) {
    const err = (payload as { error?: { code: ErrorCode; message: string; details?: unknown } })
      ?.error
    throw new ApiClientError(
      res.status,
      err?.code ?? 'INTERNAL',
      err?.message ?? 'Something went wrong.',
      err?.details,
    )
  }

  return payload as T
}
